<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { onClickOutside } from "@vueuse/core";

// Sign-in is Google only: one button, a line on what an account gives you, and a note for
// people who made an account with an email address before
const store = useStore();

const authOpen = computed(() => store.state.modals.authOpen);
const loading = computed(() => store.state.auth.loading);
const error = computed(() => store.state.auth.error);

const closeAuth = (force = false) => store.dispatch("modals/closeAuth", force);

const target = ref(null);
onClickOutside(target, () => closeAuth());

const continueWithGoogle = async () => {
  if (loading.value) return;
  const url = await store.dispatch("auth/startFederatedLogin", "Google");
  if (url) window.location.href = url;
};
</script>

<template>
  <section
    v-if="authOpen"
    class="fixed inset-0 z-50 flex items-end justify-center bg-primary/60 sm:items-center sm:p-4"
    @keydown.esc="closeAuth(true)"
  >
    <div
      ref="target"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-title"
      class="relative w-full max-w-md rounded-t-3xl bg-white px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 shadow-2xl sm:rounded-3xl sm:p-8"
    >
      <button
        type="button"
        class="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-secondary transition-colors hover:bg-gray-10 hover:text-primary"
        aria-label="Close"
        @click="closeAuth(true)"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" class="text-lg" />
      </button>
      <span
        class="mx-auto flex h-16 w-16 items-end justify-center overflow-hidden rounded-full bg-accentSecondaryLight"
        aria-hidden="true"
      >
        <img src="/genie-crossed-torso.svg" alt="" class="w-12" />
      </span>
      <h2
        id="auth-title"
        class="mt-4 text-center text-xl font-bold text-primary"
      >
        Sign in to make it
      </h2>
      <p class="mt-1 text-center text-sm text-secondary">
        Your makes are kept in your account, and new accounts get free wishes to
        start with.
      </p>
      <button
        type="button"
        class="mt-6 flex w-full items-center justify-center gap-3 rounded-2xl bg-primary px-5 py-3.5 font-bold text-white transition-colors hover:bg-secondary disabled:cursor-wait disabled:opacity-70"
        :disabled="loading"
        @click="continueWithGoogle"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
          class="h-6 w-6 rounded-full bg-white p-0.5"
          aria-hidden="true"
        >
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          />
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          />
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          />
        </svg>
        {{ loading ? "Opening Google..." : "Continue with Google" }}
      </button>
      <p
        v-if="error"
        class="mt-3 text-center text-sm font-medium text-red-500"
        role="alert"
      >
        {{ error }}
      </p>
      <p class="mt-4 text-center text-xs text-secondary">
        Made an account with your email before? Continue with Google using the
        same address and you'll carry on where you left off.
      </p>
    </div>
  </section>
</template>
